import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Building2, CircleCheck, CircleX, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HotelForm } from "./components/HotelForm";
import { Hotel as HotelBrand, HotelFilters } from "@/types/hotel";
import {
  useActivateHotel,
  useDeactivateHotel,
  useDeleteHotel,
  useHotels,
} from "./hooks/useHotels";
import { useSorting } from "@/hooks/useSorting";
import { HotelsTable } from "@/components/pages/hotels/components/HotelsTable";
import { HotelSubscriptionPlansDialog } from "@/components/pages/hotels/components/HotelSubscriptionPlansDialog";
import { HotelCopyDialog } from "@/components/pages/hotels/components/HotelCopyDialog";
import { useUsers } from "@/components/pages/users/hooks/useUsers";
import { Role } from "@/types/user";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import {
  isLimitReached,
  isPlatformAdminRole,
  isTenantAdminRole,
} from "@/utils/accessControl";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useTenantPermissions } from "@/hooks/useTenantPermissions";
import { useTenantSubscriptionPlans } from "@/components/pages/hotels/hooks/useHotelSubscriptions";
import { Input } from "@/components/ui/input";
import { useDebounce } from "@/hooks/useDebounce";
import { DateTime } from "@/components/common/DateTime";
import { MetricCard } from "@/components/common/MetricCard";
import {
  DEFAULT_HOTELS_FILTERS,
  HotelsFiltersPopover,
  type HotelsFilterValues,
} from "./components/HotelsFiltersPopover";

const perPage = 10;

type BuildHotelFiltersArgs = {
  sortField: string | null;
  sortDirection: "asc" | "desc" | null;
  isHotelRestricted: boolean;
  currentPage: number;
  tenantId?: string;
  search: string;
  filters: HotelsFilterValues;
};

const parseOptionalCount = (value: string): number | undefined =>
  value.length > 0 ? Number(value) : undefined;

const canLoadTenantSubscriptionPlans = (
  tenantId: string | undefined,
  userProfile: Parameters<typeof isTenantAdminRole>[0],
): boolean => !tenantId && isTenantAdminRole(userProfile);

const canManageTenantHotelSubscriptions = (
  canAdminHotelBrands: boolean,
  tenantId: string | undefined,
  userProfile: Parameters<typeof isTenantAdminRole>[0],
  canManageBilling: boolean,
): boolean =>
  canAdminHotelBrands &&
  !tenantId &&
  isTenantAdminRole(userProfile) &&
  canManageBilling;

function buildHotelFilters({
  sortField,
  sortDirection,
  isHotelRestricted,
  currentPage,
  tenantId,
  search,
  filters,
}: BuildHotelFiltersArgs): HotelFilters {
  const trimmedSearch = search.trim();

  return {
    sortField,
    sortDirection,
    perPage: isHotelRestricted ? 1000 : perPage,
    currentPage: isHotelRestricted ? 1 : currentPage,
    tenantId,
    search: trimmedSearch || undefined,
    status: filters.status === "all" ? undefined : filters.status,
    pms: filters.pms === "all" ? undefined : filters.pms,
    roomsMin: parseOptionalCount(filters.roomsMin),
    roomsMax: parseOptionalCount(filters.roomsMax),
    employeesMin: parseOptionalCount(filters.employeesMin),
    employeesMax: parseOptionalCount(filters.employeesMax),
    billingPlanId:
      filters.billingPlanId === "all" ? undefined : filters.billingPlanId,
  };
}

enum ViewState {
  LIST = "list",
  ADD = "add",
  EDIT = "edit",
}

export const HotelsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { sortField, sortDirection, handleSort } = useSorting();
  const [currentPage, setCurrentPage] = useState(1);
  const [currentView, setCurrentView] = useState<ViewState>(ViewState.LIST);
  const [editingBrand, setEditingBrand] = useState<HotelBrand | null>(null);
  const [subscriptionHotel, setSubscriptionHotel] = useState<HotelBrand | null>(
    null,
  );
  const [copyingBrand, setCopyingBrand] = useState<HotelBrand | null>(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<HotelsFilterValues>(
    DEFAULT_HOTELS_FILTERS,
  );
  const { userProfile } = useAuthenticatedUser();
  const { canAdminister: canAdministerTenant } = useTenantPermissions();
  const canAdminHotelBrands = canAdministerTenant("hotel_brands");
  const canCopyHotel =
    canAdminHotelBrands &&
    (isPlatformAdminRole(userProfile) || isTenantAdminRole(userProfile));
  const {
    capabilities,
    limits: { hotelsLimit },
  } = useAccessCapabilities();
  const tenantId = searchParams.get("tenantId") || undefined;
  const backTo = searchParams.get("backTo");
  const canManageHotelSubscriptions = canManageTenantHotelSubscriptions(
    canAdminHotelBrands,
    tenantId,
    userProfile ?? null,
    capabilities.canManageBilling,
  );
  const canLoadSubscriptionPlans = canLoadTenantSubscriptionPlans(
    tenantId,
    userProfile ?? null,
  );
  const {
    data: subscriptionPlans = [],
    isLoading: isLoadingSubscriptionPlans,
  } = useTenantSubscriptionPlans(canLoadSubscriptionPlans);

  const allowedHotelIds = userProfile?.hotelIds ?? [];
  const isHotelRestricted =
    !isTenantAdminRole(userProfile ?? null) &&
    userProfile?.hotelIds !== undefined;
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filters, tenantId]);

  const { data: hotelsResponse, isLoading } = useHotels(
    buildHotelFilters({
      sortField,
      sortDirection,
      isHotelRestricted,
      currentPage,
      tenantId,
      search: debouncedSearch,
      filters,
    }),
  );

  const deleteHotelMutation = useDeleteHotel();
  const activateHotelMutation = useActivateHotel();
  const deactivateHotelMutation = useDeactivateHotel();

  const allFetchedHotels = hotelsResponse?.data ?? [];
  const filteredHotels = isHotelRestricted
    ? allFetchedHotels.filter((h) => allowedHotelIds.includes(h.id))
    : allFetchedHotels;
  const hotels = isHotelRestricted
    ? filteredHotels.slice((currentPage - 1) * perPage, currentPage * perPage)
    : filteredHotels;
  const totalHotels = isHotelRestricted
    ? filteredHotels.length
    : (hotelsResponse?.total ?? filteredHotels.length);
  const hotelSummary = hotelsResponse?.summary ?? {
    total: totalHotels,
    active: filteredHotels.filter(
      (hotel) => hotel.isEnabled ?? hotel.isActive ?? true,
    ).length,
    inactive: filteredHotels.filter(
      (hotel) => !(hotel.isEnabled ?? hotel.isActive ?? true),
    ).length,
  };
  const hotelLimitReached = isLimitReached(totalHotels, hotelsLimit);
  const { data: managers } = useUsers({
    roles: [Role.TENANT_ADMIN, Role.HOTEL_ADMIN],
    tenantId,
  });

  const handleEditBrand = (brand: HotelBrand) => {
    setEditingBrand(brand);
    setCurrentView(ViewState.EDIT);
  };

  const handleBackToList = () => {
    setCurrentView(ViewState.LIST);
    setEditingBrand(null);
  };

  const handleDeleteBrand = async (brandId: string) => {
    try {
      await deleteHotelMutation.mutateAsync(brandId);
      toast.success(t("hotels.page.hotelDeletedSuccessfully"));
    } catch (error) {
      toast.error(t("hotels.page.failedToDeleteHotel"));
      console.error("Error deleting hotel:", error);
      throw error;
    }
  };

  const handleActivateBrand = async (brandId: string) => {
    try {
      await activateHotelMutation.mutateAsync(brandId);
      toast.success(t("hotels.page.hotelActivatedSuccessfully"));
    } catch (error) {
      toast.error(t("hotels.page.failedToActivateHotel"));
      console.error("Error activating hotel:", error);
      throw error;
    }
  };

  const handleDeactivateBrand = async (brandId: string) => {
    try {
      await deactivateHotelMutation.mutateAsync(brandId);
      toast.success(t("hotels.page.hotelDeactivatedSuccessfully"));
    } catch (error) {
      toast.error(t("hotels.page.failedToDeactivateHotel"));
      console.error("Error deactivating hotel:", error);
      throw error;
    }
  };

  const handleManageSubscription = (hotel: HotelBrand) => {
    if (canManageHotelSubscriptions) {
      setSubscriptionHotel(hotel);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalHotels / perPage));
  if (currentView === ViewState.ADD) {
    return (
      <HotelForm
        managers={managers}
        onBack={handleBackToList}
        tenantId={tenantId}
      />
    );
  }

  if (currentView === ViewState.EDIT && editingBrand) {
    return (
      <HotelForm
        hotel={editingBrand}
        managers={managers}
        onBack={handleBackToList}
        tenantId={tenantId}
      />
    );
  }

  return (
    <div className="mx-auto flex max-w-[1344px] flex-col space-y-6 p-12">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {backTo ? (
            <Button variant="new_outline" onClick={() => navigate(backTo)}>
              {t("hotels.page.back")}
            </Button>
          ) : null}
        </div>
        <DateTime className="hidden lg:flex" />
        <div className="flex items-center justify-between">
          {!tenantId ? (
            <Button
              variant="new_default"
              onClick={() => setCurrentView(ViewState.ADD)}
              disabled={!canAdminHotelBrands || hotelLimitReached}
              title={
                !canAdminHotelBrands
                  ? t(
                      "common.insufficientPermissions",
                      "Insufficient permissions",
                    )
                  : undefined
              }
            >
              <Plus />
              {t("hotels.page.addHotel")}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <MetricCard
          width="fill"
          title={t("hotels.metrics.total", "Total hotel brands")}
          value={String(hotelSummary.total)}
          icon={<Building2 />}
        />
        <MetricCard
          width="fill"
          title={t("hotels.metrics.active", "Active hotel brands")}
          value={String(hotelSummary.active)}
          icon={<CircleCheck />}
        />
        <MetricCard
          width="fill"
          title={t("hotels.metrics.inactive", "Inactive hotel brands")}
          value={String(hotelSummary.inactive)}
          icon={<CircleX />}
        />
      </div>

      {hotelLimitReached ? (
        <p className="text-muted-foreground text-sm">
          {t("hotels.page.hotelLimitReached", {
            limit: hotelsLimit,
          })}
        </p>
      ) : null}

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("hotels.page.searchPlaceholder")}
          icon={<Search className="size-4 text-black/45" />}
          className="h-10"
        />
        <HotelsFiltersPopover
          applied={filters}
          billingPlans={subscriptionPlans}
          isBillingPlanFilterDisabled={
            !canLoadSubscriptionPlans || isLoadingSubscriptionPlans
          }
          onApply={setFilters}
        />
      </div>

      <HotelsTable
        currentPage={currentPage}
        totalPages={totalPages}
        handleDeleteBrand={handleDeleteBrand}
        isDeletingHotel={deleteHotelMutation.isPending}
        canDeleteHotels={canAdminHotelBrands}
        handleActivateBrand={handleActivateBrand}
        handleDeactivateBrand={handleDeactivateBrand}
        isChangingHotelStatus={
          activateHotelMutation.isPending || deactivateHotelMutation.isPending
        }
        handleEditBrand={handleEditBrand}
        canEditHotels={canAdminHotelBrands}
        handleCopyBrand={setCopyingBrand}
        canCopyHotels={canCopyHotel}
        hotels={hotels}
        handleSort={handleSort}
        isLoading={isLoading}
        setCurrentPage={setCurrentPage}
        sortField={sortField}
        sortDirection={sortDirection}
        onManageSubscription={handleManageSubscription}
        canManageSubscriptions={canManageHotelSubscriptions}
        subscriptionPlans={subscriptionPlans}
      />
      <HotelSubscriptionPlansDialog
        hotel={canManageHotelSubscriptions ? subscriptionHotel : null}
        canManage={canManageHotelSubscriptions}
        onClose={() => setSubscriptionHotel(null)}
      />

      <HotelCopyDialog
        hotel={copyingBrand}
        onClose={() => setCopyingBrand(null)}
      />
    </div>
  );
};
